import { useTranslation } from 'react-i18next';
import { getRouteApi } from '@tanstack/react-router';

import { useShelfDetections } from '@/api/generated';
import PageLayout from '@/components/blocks/layout/page-layout';
import {
  NavBreadcrumb,
  NavBreadcrumbItem,
} from '@/components/blocks/shared/nav-breadcrumb';
import { ShelfDetailSkeleton } from '@/components/blocks/shelf-detail/shelf-detail-skeleton';
import { ShelfDetailWorkspace } from '@/components/blocks/shelf-detail/shelf-detail-workspace';
import { ShelfStatusControls } from '@/components/blocks/shelf-detail/shelf-status-controls';
import { useShelfStatus } from '@/components/blocks/shelf-detail/use-shelf-status';
import { CommonError } from '@/components/ui/common-error';
import { cn } from '@/lib/utils';

const route = getRouteApi('/app/shelves/$customerId/$submissionId/$shelveId');

const ShelfDetail = () => {
  const { t } = useTranslation();
  const { customerId, submissionId, shelveId } = route.useParams();
  const { data, isLoading, isError } = useShelfDetections(
    submissionId,
    shelveId,
    { query: { gcTime: 0 } }
  );
  const { setStatus, isPending } = useShelfStatus({ submissionId, shelveId });

  const navigationCrumbs: Array<NavBreadcrumbItem> = [
    {
      text: t('customers.title'),
      path: '/app/shelves',
    },
    {
      text: `${customerId}`,
      path: '/app/shelves/$customerId',
      params: { customerId },
    },
    {
      text: t('shelves.title', { id: submissionId }),
      path: '/app/shelves/$customerId/$submissionId',
      params: { customerId, submissionId },
    },
    {
      text: t('shelfDetail.title', { id: shelveId }),
      path: '/app/shelves/$customerId/$submissionId/$shelveId',
      params: { customerId, submissionId, shelveId },
    },
  ];

  if (isLoading) {
    return <ShelfDetailSkeleton className='workspace-height' />;
  }

  if (isError || !data) {
    return <CommonError />;
  }

  return (
    <PageLayout className={cn('flex flex-col gap-2')}>
      <div className='page-top-container'>
        <span className='page-top-title'>{t('shelfDetail.title')}</span>

        <div className='flex items-center gap-4'>
          <NavBreadcrumb crumbs={navigationCrumbs} />
          <ShelfStatusControls
            customerId={customerId}
            submissionId={submissionId}
            shelveId={shelveId}
            status={data.status}
            setStatus={setStatus}
            isPending={isPending}
          />
        </div>
      </div>
      {/* Keyed so save-and-next resets workspace state when only the param changes. */}
      <ShelfDetailWorkspace
        key={shelveId}
        detections={data.items}
        imagePath={data.imagePath}
        readonly={data.status === 'annotation_completed'}
        onReEdit={async () => {
          await setStatus('annotation_in_progress');
        }}
        reEditPending={isPending}
      />
    </PageLayout>
  );
};

export default ShelfDetail;
